/**
 * Editor visual de modelos Word: o documento aparece como uma folha; o
 * administrador seleciona um trecho (ou clica num espaço) e escolhe no menu o
 * dado do sistema, uma regra ("mostrar só quando…"), concordância ou
 * repetição. Nada de código: as marcações viram etiquetas coloridas.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Badge, EntityPicker, ErrorBox, Field, FormGrid, Loading, Modal, useAct, type EntityRef } from "./kit";
import { confirmar } from "./feedback";
import { Paper, type Block, type InputDef } from "./generator";

// ------------------------------------------------------------------ tipos

interface Seg { text: string; s: number | null; b?: boolean; i?: boolean; u?: boolean }
interface OPara {
  id: string; part: string; idx: number; text: string; segs: Seg[];
  align: "left" | "center" | "right" | "justify"; indent: boolean; heading: 0 | 1 | 2; size: number | null;
  table: { t: number; r: number; c: number } | null; pageBreak: boolean; image: boolean;
}
interface Suggestion { part: string; idx: number; start: number; end: number; sample: string; context: string; field: string | null; ask?: { label: string; type: "texto" | "dinheiro" | "data" } }
interface Rule { id: string; kind: string; value?: string | null; label: string }
interface EditorData {
  template: {
    id: string; name: string; description: string | null; context: "pessoa" | "caso" | "processo"; kind: string; fileName: string | null;
    letterhead: boolean; letterheadId: string | null; required: string[]; version: number; pdfEngine: "word" | "interno"; active: boolean;
  };
  outline: OPara[]; suggestions: Suggestion[]; rules: Rule[]; inputs: InputDef[];
  versions: Array<{ version: number; note: string | null; createdAt: string; createdBy: string; kind: string }>;
  catalog: {
    groups: Array<{ label: string; fields: Array<{ key: string; label: string }> }>;
    items: Record<string, Record<string, string>>; ruleKinds: Record<string, string>; actionTypes: string[];
  };
}
interface Edit { part: string; idx: number; start: number; end: number; text: string }
type Sel =
  | { kind: "range"; part: string; idx: number; start: number; end: number; text: string; rect: DOMRect }
  | { kind: "caret"; part: string; idx: number; at: number; rect: DOMRect }
  | { kind: "multi"; part: string; from: number; to: number; rect: DOMRect };
interface Tag { part: string; idx: number; start: number; end: number; ctl: string | undefined; name: string }

const TAG_RE = /\{\{\s*(#senao|#se|\/senao|\/se|#cada|\/cada)?\s*([^{}]*?)\s*\}\}/g;
const LIST_LABEL: Record<string, string> = { clientes: "cliente", parceiros: "advogado parceiro" };
const FILTER_LABEL: Record<string, string> = { maiusculas: "MAIÚSCULAS", minusculas: "minúsculas" };

function tagsOf(p: OPara): Tag[] {
  return [...p.text.matchAll(TAG_RE)].map((m) => ({ part: p.part, idx: p.idx, start: m.index!, end: m.index! + m[0].length, ctl: m[1], name: m[2]! }));
}

/** Pares abre/fecha (regras e repetições) em ordem de leitura, por parte. */
function pairsOf(outline: OPara[]): Array<{ open: Tag; close: Tag | null }> {
  const out: Array<{ open: Tag; close: Tag | null }> = [];
  const byPart = new Map<string, OPara[]>();
  for (const p of outline) byPart.set(p.part, [...(byPart.get(p.part) ?? []), p]);
  for (const paras of byPart.values()) {
    const stack: Tag[] = [];
    for (const p of paras) for (const t of tagsOf(p)) {
      if (t.ctl === "#se" || t.ctl === "#senao" || t.ctl === "#cada") stack.push(t);
      else if (t.ctl === "/se" || t.ctl === "/senao" || t.ctl === "/cada") {
        const open = stack.pop();
        if (open) out.push({ open, close: t });
      }
    }
    for (const o of stack) out.push({ open: o, close: null });
  }
  return out;
}

function guessGender(sel: string): [string, string, string] {
  const s = sel.trim();
  const pa = s.match(/^(.*)\(a\)$/);
  if (pa) { const b = pa[1]!; return [b, b.endsWith("o") ? `${b.slice(0, -1)}a` : `${b}a`, ""]; }
  if (/or$/.test(s)) return [s, `${s}a`, `${s}es`];
  if (/ora$/.test(s)) return [s.slice(0, -1), s, `${s.slice(0, -1)}es`];
  if (/o$/.test(s)) return [s, `${s.slice(0, -1)}a`, `${s}s`];
  if (/a$/.test(s)) return [`${s.slice(0, -1)}o`, s, `${s.slice(0, -1)}os`];
  return [s, s, ""];
}

// ================================================================ editor

export function TemplateEditor({ id, onClose }: { id: string; onClose: () => void }): JSX.Element {
  const qc = useQueryClient();
  const { data, error, isLoading } = useQuery({ queryKey: ["modelo-editor", id], queryFn: () => api.get<EditorData>(`/config/modelos/${id}/editor`) });
  const [sel, setSel] = useState<Sel | null>(null);
  const [dialog, setDialog] = useState<null | "campo" | "regra" | "genero" | "repetir" | "props" | "teste">(null);
  const [replaceTag, setReplaceTag] = useState<Tag | null>(null);
  const [chip, setChip] = useState<{ tag: Tag; rect: DOMRect } | null>(null);
  const [panel, setPanel] = useState<"sugestoes" | "regras" | "historico">("sugestoes");
  const [ignored, setIgnored] = useState<Set<string>>(new Set());
  const docRef = useRef<HTMLDivElement>(null);

  const save = useAct((body: { edits: Edit[]; rule?: Omit<Rule, "id" | "label"> & { label?: string } | null; input?: InputDef | null; note: string }) =>
    api.post<{ version: number }>(`/config/modelos/${id}/editor`, body), [["modelo-editor", id], ["config-modelos"], ["modelos-documento"]],
  () => { setSel(null); setDialog(null); setChip(null); setReplaceTag(null); window.getSelection()?.removeAllRanges(); });
  const restore = useAct((version?: number) => api.post(`/config/modelos/${id}/editor/restaurar`, version ? { version } : {}),
    [["modelo-editor", id], ["config-modelos"]], () => { setSel(null); setChip(null); }, "Versão restaurada");

  const labels = useMemo(() => {
    const m = new Map<string, string>();
    for (const g of data?.catalog.groups ?? []) for (const f of g.fields) m.set(f.key, f.label);
    for (const i of data?.inputs ?? []) {
      m.set(`campo.${i.key}`, i.label);
      if (i.type === "dinheiro" || i.type === "data") m.set(`campo.${i.key}_extenso`, `${i.label} (por extenso)`);
    }
    for (const [list, fields] of Object.entries(data?.catalog.items ?? {})) for (const [k, l] of Object.entries(fields)) m.set(`item.${k}`, `${l} de cada ${LIST_LABEL[list] ?? list}`);
    return m;
  }, [data]);
  const ruleOf = (name: string) => data?.rules.find((r) => `regra.${r.id}` === name);

  const chipText = (t: Tag): { text: string; cls: string } => {
    const [key = "", filter] = t.name.split("|").map((x) => x.trim());
    if (t.ctl === "#se" || t.ctl === "#senao") {
      const r = ruleOf(t.name);
      const what = r ? r.label : `${labels.get(t.name) ?? t.name} preenchido`;
      return { text: `${t.ctl === "#se" ? "▶ Só se" : "▶ Senão"}: ${what}`, cls: "rule" };
    }
    if (t.ctl === "/se" || t.ctl === "/senao") return { text: "◀ fim da regra", cls: "rule" };
    if (t.ctl === "#cada") return { text: `⟳ Para cada ${LIST_LABEL[t.name] ?? t.name}`, cls: "loop" };
    if (t.ctl === "/cada") return { text: "⟲ fim da repetição", cls: "loop" };
    if (t.name.startsWith("g:")) { const [m, f] = t.name.slice(2).split("|"); return { text: `${m}/${f}`, cls: "gender" }; }
    return { text: `${labels.get(key) ?? key}${filter && FILTER_LABEL[filter] ? ` (${FILTER_LABEL[filter]})` : ""}`, cls: "field" };
  };

  // --------------------------------------------------------- seleção
  const posOf = (node: Node, offset: number, edge: "start" | "end"): { part: string; idx: number; at: number } | null => {
    const el = (node.nodeType === 3 ? node.parentElement : node as Element) as HTMLElement | null;
    const para = el?.closest<HTMLElement>("[data-idx]");
    if (!el || !para) return null;
    const part = para.dataset.part!, idx = Number(para.dataset.idx);
    const chipEl = el.closest<HTMLElement>("[data-tag-start]");
    if (chipEl) return { part, idx, at: Number(edge === "start" ? chipEl.dataset.tagStart : chipEl.dataset.tagEnd) };
    const span = el.closest<HTMLElement>("[data-s]");
    if (span && node.nodeType === 3) return { part, idx, at: Number(span.dataset.s) + offset };
    // Nó do parágrafo: posição = começo do filho `offset` ou o fim do texto.
    const kids = [...(el === para ? para.childNodes : el.childNodes)] as HTMLElement[];
    for (let k = offset; k < kids.length; k++) {
      const s = (kids[k] as HTMLElement).dataset?.s ?? (kids[k] as HTMLElement).dataset?.tagStart;
      if (s !== undefined) return { part, idx, at: Number(s) };
    }
    const p = data?.outline.find((x) => x.part === part && x.idx === idx);
    return { part, idx, at: p?.text.length ?? 0 };
  };

  const onMouseUp = () => {
    const s = window.getSelection();
    if (!s || !s.rangeCount || !docRef.current) return;
    const r = s.getRangeAt(0);
    if (!docRef.current.contains(r.commonAncestorContainer)) return;
    const a = posOf(r.startContainer, r.startOffset, "start"), b = posOf(r.endContainer, r.endOffset, "end");
    if (!a || !b) return;
    const rect = r.getBoundingClientRect().width || r.getBoundingClientRect().height ? r.getBoundingClientRect() : (r.startContainer.parentElement ?? docRef.current).getBoundingClientRect();
    setChip(null);
    if (a.part !== b.part) { setSel(null); return; }
    if (a.idx !== b.idx) { setSel({ kind: "multi", part: a.part, from: Math.min(a.idx, b.idx), to: Math.max(a.idx, b.idx), rect }); return; }
    const p = data!.outline.find((x) => x.part === a.part && x.idx === a.idx)!;
    const start = Math.min(a.at, b.at), end = Math.max(a.at, b.at);
    if (start === end) setSel({ kind: "caret", part: a.part, idx: a.idx, at: start, rect });
    else setSel({ kind: "range", part: a.part, idx: a.idx, start, end, text: p.text.slice(start, end), rect });
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !dialog) { if (sel || chip) { setSel(null); setChip(null); } else onClose(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dialog, sel, chip, onClose]);

  if (isLoading) return <div className="tpl-editor"><div className="tpl-body"><Loading lines={10} /></div></div>;
  if (error || !data) return <div className="tpl-editor"><div className="tpl-top"><b>Editor</b><span className="spacer" /><button className="btn" onClick={onClose}>Fechar</button></div><div className="tpl-body"><ErrorBox error={error} /></div></div>;

  const pairs = pairsOf(data.outline);
  const removeTag = (t: Tag) => {
    const pair = pairs.find((p) => (p.open.idx === t.idx && p.open.start === t.start && p.open.part === t.part) || (p.close && p.close.idx === t.idx && p.close.start === t.start && p.close.part === t.part));
    const tags = pair ? [pair.open, ...(pair.close ? [pair.close] : [])] : [t];
    // Regra com "senão": o bloco alternativo vem logo depois e sai junto.
    const extra = pair && pair.open.ctl === "#se" ? pairs.find((p) => p.open.ctl === "#senao" && p.open.name === pair.open.name && p.open.part === pair.open.part && pair.close && p.open.idx === pair.close.idx && p.open.start === pair.close.end) : undefined;
    const edits: Edit[] = tags.map((x) => ({ part: x.part, idx: x.idx, start: x.start, end: x.end, text: "" }));
    if (extra?.close) edits.push({ part: extra.open.part, idx: extra.open.idx, start: extra.open.start, end: extra.close.end, text: "" });
    save.mutate({ edits: dedupe(edits), note: "Marcação removida" });
  };
  const apply = (edits: Edit[], note: string, extra: { rule?: Omit<Rule, "id" | "label">; input?: InputDef } = {}) => save.mutate({ edits, note, ...extra });

  const pending = data.suggestions.filter((s) => !ignored.has(`${s.part}#${s.idx}:${s.start}`));
  const docParts = [...new Set(data.outline.map((p) => p.part))];

  return (
    <div className="tpl-editor">
      <div className="tpl-top">
        <div className="grow">
          <b>{data.template.name}</b> <span className="muted small">versão {data.template.version} · {data.template.fileName}</span>
          {!data.template.active && <> <Badge tone="muted">desativado</Badge></>}
        </div>
        <button className="btn small" disabled={restore.isPending || data.versions.length < 2} onClick={() => restore.mutate(undefined)} title="Volta para a versão anterior">↶ Desfazer</button>
        <button className="btn small" onClick={() => setDialog("teste")}>Testar</button>
        <button className="btn small" onClick={() => setDialog("props")}>Propriedades</button>
        <button className="btn small" onClick={() => void api.download(`/config/modelos/${id}/arquivo`, data.template.fileName ?? "modelo.docx")}>Baixar Word</button>
        <button className="btn small primary" onClick={onClose}>Concluir</button>
      </div>
      <div className="tpl-body">
        <div className="tpl-doc-wrap" onMouseUp={onMouseUp}>
          <div className="tpl-hint">Selecione um trecho (ou clique num espaço) e escolha o que fazer. Selecione vários parágrafos para uma regra ou repetição.</div>
          <div ref={docRef} className="tpl-page">
            {docParts.map((part) => (
              <div key={part} className={`tpl-part ${part === "document" ? "" : "tpl-hf"}`}>
                {part !== "document" && <div className="tpl-part-label">{part.startsWith("header") ? "Cabeçalho" : "Rodapé"}</div>}
                <DocParas paras={data.outline.filter((p) => p.part === part)} chipText={chipText}
                  onChip={(tag, rect) => { setSel(null); setChip({ tag, rect }); }} />
              </div>
            ))}
            {!data.outline.length && <div className="muted">Documento vazio.</div>}
          </div>
        </div>

        <aside className="tpl-side">
          <div className="tabs">
            {([["sugestoes", `Sugestões (${pending.length})`], ["regras", `Regras (${data.rules.length})`], ["historico", "Histórico"]] as const).map(([k, l]) => (
              <button key={k} className={`tab ${panel === k ? "active" : ""}`} onClick={() => setPanel(k)}>{l}</button>
            ))}
          </div>
          {panel === "sugestoes" && (
            <Suggestions items={pending} labels={labels}
              onApply={(list) => apply(list.map((s) => ({ part: s.part, idx: s.idx, start: s.start, end: s.end, text: `{{${s.field}}}` })), list.length > 1 ? `${list.length} sugestões aplicadas` : `Vinculado: ${labels.get(list[0]!.field!) ?? list[0]!.field}`)}
              onIgnore={(s) => setIgnored(new Set([...ignored, `${s.part}#${s.idx}:${s.start}`]))}
              onPick={(s) => { setSel({ kind: "range", part: s.part, idx: s.idx, start: s.start, end: s.end, text: s.sample, rect: new DOMRect(0, 0, 0, 0) }); setDialog("campo"); }} />
          )}
          {panel === "regras" && (
            <div className="stack gap-8">
              {!data.rules.length && <div className="muted small">Nenhuma regra. Selecione um trecho e clique em “Mostrar só quando…”.</div>}
              {data.rules.map((r) => {
                const uses = pairs.filter((p) => p.open.name === `regra.${r.id}`);
                return (
                  <div key={r.id} className="lrow">
                    <span className="grow small"><b>Só se</b> {r.label}<div className="muted">{uses.length} trecho(s)</div></span>
                    <button className="btn small ghost" onClick={() => void confirmar("Remover esta regra? O texto continua no documento, sempre visível.").then((y) => {
                      if (!y) return;
                      const edits: Edit[] = [];
                      for (const u of uses) { edits.push({ part: u.open.part, idx: u.open.idx, start: u.open.start, end: u.open.end, text: "" }); if (u.close) edits.push({ part: u.close.part, idx: u.close.idx, start: u.close.start, end: u.close.end, text: "" }); }
                      // Blocos "senão" da regra saem inteiros.
                      const alt = uses.filter((u) => u.open.ctl === "#senao" && u.close);
                      const edits2 = edits.filter((e) => !alt.some((a) => a.open.part === e.part && ((a.open.idx === e.idx && a.open.start === e.start) || (a.close!.idx === e.idx && a.close!.start === e.start))));
                      for (const a of alt) if (a.open.idx === a.close!.idx) edits2.push({ part: a.open.part, idx: a.open.idx, start: a.open.start, end: a.close!.end, text: "" });
                      save.mutate({ edits: dedupe(edits2), note: `Regra removida: ${r.label}` });
                    })}>Remover</button>
                  </div>
                );
              })}
              <div className="muted small">Dica: para mostrar um texto alternativo, use “Senão, mostrar…” ao criar a regra.</div>
            </div>
          )}
          {panel === "historico" && (
            <div className="stack gap-4">
              {data.versions.map((v) => (
                <div key={v.version} className="lrow">
                  <span className="grow small"><b>v{v.version}</b> {v.note ?? ""}<div className="muted">{fmtDateTime(v.createdAt)}</div></span>
                  {v.version !== data.template.version && <button className="btn small ghost" onClick={() => void confirmar(`Voltar para a versão ${v.version}? A atual fica guardada no histórico.`).then((y) => y && restore.mutate(v.version))}>Restaurar</button>}
                </div>
              ))}
            </div>
          )}
          <ErrorBox error={save.error ?? restore.error} />
        </aside>
      </div>

      {sel && !dialog && (
        <div className="tpl-toolbar" style={{ top: Math.max(60, sel.rect.top - 48), left: Math.min(window.innerWidth - 520, Math.max(12, sel.rect.left)) }} onMouseDown={(e) => e.preventDefault()}>
          {sel.kind === "range" && <>
            <button className="btn small primary" onClick={() => setDialog("campo")}>Vincular dado</button>
            <button className="btn small" onClick={() => setDialog("regra")}>Mostrar só quando…</button>
            <button className="btn small" onClick={() => setDialog("genero")}>Concordância</button>
          </>}
          {sel.kind === "caret" && <>
            <button className="btn small primary" onClick={() => setDialog("campo")}>Inserir dado aqui</button>
            <button className="btn small" onClick={() => setDialog("genero")}>Inserir concordância</button>
          </>}
          {sel.kind === "multi" && <>
            <button className="btn small primary" onClick={() => setDialog("regra")}>Mostrar estes parágrafos só quando…</button>
            <button className="btn small" onClick={() => setDialog("repetir")}>Repetir para cada…</button>
          </>}
          <button className="btn small ghost" onClick={() => { setSel(null); window.getSelection()?.removeAllRanges(); }}>✕</button>
        </div>
      )}

      {chip && !dialog && (
        <div className="tpl-toolbar" style={{ top: Math.max(60, chip.rect.top - 48), left: Math.min(window.innerWidth - 420, Math.max(12, chip.rect.left)) }}>
          <span className="small strong">{chipText(chip.tag).text}</span>
          {!chip.tag.ctl && !chip.tag.name.startsWith("g:") && <button className="btn small" onClick={() => { setReplaceTag(chip.tag); setSel({ kind: "range", part: chip.tag.part, idx: chip.tag.idx, start: chip.tag.start, end: chip.tag.end, text: "", rect: chip.rect }); setDialog("campo"); }}>Trocar dado</button>}
          <button className="btn small" onClick={() => removeTag(chip.tag)}>{chip.tag.ctl ? (chip.tag.ctl.includes("cada") ? "Remover repetição" : "Remover regra") : "Remover"}</button>
          <button className="btn small ghost" onClick={() => setChip(null)}>✕</button>
        </div>
      )}

      {dialog === "campo" && sel && (sel.kind === "range" || sel.kind === "caret") && (
        <FieldPicker data={data} insideLoop={loopAt(pairs, sel.part, sel.idx, sel.kind === "range" ? sel.start : sel.at)} sample={sel.kind === "range" ? sel.text : ""}
          title={replaceTag ? "Trocar dado" : sel.kind === "caret" ? "Inserir dado" : "Vincular dado ao trecho"}
          busy={save.isPending} onClose={() => { setDialog(null); setReplaceTag(null); }}
          onPick={(key, filter, input) => {
            const text = `{{${key}${filter ? `|${filter}` : ""}}}`;
            const e: Edit = sel.kind === "range" ? { part: sel.part, idx: sel.idx, start: sel.start, end: sel.end, text } : { part: sel.part, idx: sel.idx, start: sel.at, end: sel.at, text };
            apply([e], `Vinculado: ${input?.label ?? labels.get(key) ?? key}`, input ? { input } : {});
          }} />
      )}
      {dialog === "genero" && sel && (sel.kind === "range" || sel.kind === "caret") && (
        <GenderDialog sample={sel.kind === "range" ? sel.text : ""} busy={save.isPending} onClose={() => setDialog(null)}
          onSave={(m, f, pl) => {
            const text = `{{g:${m}|${f}${pl ? `|${pl}` : ""}}}`;
            const e: Edit = sel.kind === "range" ? { part: sel.part, idx: sel.idx, start: sel.start, end: sel.end, text } : { part: sel.part, idx: sel.idx, start: sel.at, end: sel.at, text };
            apply([e], `Concordância: ${m}/${f}`);
          }} />
      )}
      {dialog === "regra" && sel && (sel.kind === "range" || sel.kind === "multi") && (
        <RuleDialog data={data} busy={save.isPending} onClose={() => setDialog(null)}
          onSave={(rule, alt, input) => {
            const close = `{{/se}}${alt ? `{{#senao @REGRA@}}${alt}{{/senao}}` : ""}`;
            let edits: Edit[];
            if (sel.kind === "range") edits = [
              { part: sel.part, idx: sel.idx, start: sel.end, end: sel.end, text: close },
              { part: sel.part, idx: sel.idx, start: sel.start, end: sel.start, text: "{{#se @REGRA@}}" },
            ];
            else {
              const last = data.outline.find((p) => p.part === sel.part && p.idx === sel.to)!;
              edits = [
                { part: sel.part, idx: sel.to, start: last.text.length, end: last.text.length, text: close },
                { part: sel.part, idx: sel.from, start: 0, end: 0, text: "{{#se @REGRA@}}" },
              ];
            }
            apply(edits, "Regra criada", { rule, ...(input ? { input } : {}) });
          }} />
      )}
      {dialog === "repetir" && sel && sel.kind === "multi" && (
        <Modal title="Repetir estes parágrafos" onClose={() => setDialog(null)}>
          <div className="stack gap-8">
            <div className="small">Os parágrafos selecionados saem uma vez para cada item. Dentro deles, use <b>Vincular dado › Cada cliente/parceiro</b> (nome, qualificação, OAB…).</div>
            {Object.keys(LIST_LABEL).map((k) => (
              <button key={k} className="btn" disabled={save.isPending} onClick={() => {
                const last = data.outline.find((p) => p.part === sel.part && p.idx === sel.to)!;
                apply([
                  { part: sel.part, idx: sel.to, start: last.text.length, end: last.text.length, text: "{{/cada}}" },
                  { part: sel.part, idx: sel.from, start: 0, end: 0, text: `{{#cada ${k}}}` },
                ], `Repetição por ${LIST_LABEL[k]}`);
              }}>Para cada {LIST_LABEL[k]}</button>
            ))}
          </div>
        </Modal>
      )}
      {dialog === "props" && <PropsDialog data={data} onClose={() => { setDialog(null); void qc.invalidateQueries({ queryKey: ["modelo-editor", id] }); }} />}
      {dialog === "teste" && <TestDialog data={data} onClose={() => setDialog(null)} />}
    </div>
  );
}

function dedupe(edits: Edit[]): Edit[] {
  const seen = new Set<string>();
  return edits.filter((e) => { const k = `${e.part}#${e.idx}:${e.start}:${e.end}`; if (seen.has(k)) return false; seen.add(k); return true; });
}

function loopAt(pairs: Array<{ open: Tag; close: Tag | null }>, part: string, idx: number, at: number): string | null {
  const after = (t: Tag) => t.part === part && (idx > t.idx || (idx === t.idx && at >= t.end));
  const before = (t: Tag) => t.part === part && (idx < t.idx || (idx === t.idx && at <= t.start));
  const p = pairs.find((x) => x.open.ctl === "#cada" && x.close && after(x.open) && before(x.close));
  return p ? p.open.name : null;
}

// ------------------------------------------------------------- folha

function DocParas({ paras, chipText, onChip }: {
  paras: OPara[]; chipText: (t: Tag) => { text: string; cls: string }; onChip: (t: Tag, rect: DOMRect) => void;
}): JSX.Element {
  const out: JSX.Element[] = [];
  for (let i = 0; i < paras.length; i++) {
    const p = paras[i]!;
    if (p.table) {
      const t = p.table.t;
      const cells: OPara[][][] = [];
      let j = i;
      while (j < paras.length && paras[j]!.table?.t === t) {
        const q = paras[j]!;
        const row = (cells[q.table!.r] ??= []);
        (row[q.table!.c] ??= []).push(q);
        j++;
      }
      out.push(
        <table key={`t${p.id}`} className="tpl-table"><tbody>
          {cells.map((row, r) => <tr key={r}>{row.map((cell, c) => <td key={c}>{(cell ?? []).map((q) => <DocPara key={q.id} p={q} chipText={chipText} onChip={onChip} />)}</td>)}</tr>)}
        </tbody></table>,
      );
      i = j - 1;
      continue;
    }
    out.push(<DocPara key={p.id} p={p} chipText={chipText} onChip={onChip} />);
  }
  return <>{out}</>;
}

function DocPara({ p, chipText, onChip }: { p: OPara; chipText: (t: Tag) => { text: string; cls: string }; onChip: (t: Tag, rect: DOMRect) => void }): JSX.Element {
  const tags = tagsOf(p);
  const pieces: JSX.Element[] = [];
  const shown = new Set<number>();
  let k = 0;
  for (const seg of p.segs) {
    if (seg.s === null) { pieces.push(seg.text === "\t" ? <span key={k++} className="tpl-tab">{" "}</span> : <br key={k++} />); continue; }
    const a = seg.s, b = seg.s + seg.text.length;
    let cur = a;
    while (cur < b) {
      const tag = tags.find((t) => t.start <= cur && cur < t.end);
      if (tag) {
        if (!shown.has(tag.start)) {
          shown.add(tag.start);
          const c = chipText(tag);
          pieces.push(
            <span key={k++} className={`tpl-chip ${c.cls}`} contentEditable={false} data-tag-start={tag.start} data-tag-end={tag.end}
              onMouseUp={(e) => { e.stopPropagation(); onChip(tag, (e.currentTarget as HTMLElement).getBoundingClientRect()); }}>{c.text}</span>,
          );
        }
        cur = Math.min(b, tag.end);
        continue;
      }
      const next = Math.min(b, ...tags.filter((t) => t.start > cur).map((t) => t.start));
      const style = { fontWeight: seg.b ? 700 : undefined, fontStyle: seg.i ? "italic" : undefined, textDecoration: seg.u ? "underline" : undefined } as const;
      pieces.push(<span key={k++} data-s={cur} style={style}>{seg.text.slice(cur - a, next - a)}</span>);
      cur = next;
    }
  }
  const cls = `tpl-p al-${p.align}${p.indent ? " ind" : ""}${p.heading === 1 ? " h1" : p.heading === 2 ? " h2" : ""}`;
  return (
    <>
      {p.pageBreak && <div className="tpl-pagebreak">nova página</div>}
      <p className={cls} data-part={p.part} data-idx={p.idx}>
        {p.image && <span className="tpl-img" contentEditable={false}>[imagem]</span>}
        {pieces.length ? pieces : <span data-s={0}>{"​"}</span>}
      </p>
    </>
  );
}

// ------------------------------------------------------------ sugestões

function Suggestions({ items, labels, onApply, onIgnore, onPick }: {
  items: Suggestion[]; labels: Map<string, string>;
  onApply: (s: Suggestion[]) => void; onIgnore: (s: Suggestion) => void; onPick: (s: Suggestion) => void;
}): JSX.Element {
  const ready = items.filter((s) => s.field);
  if (!items.length) return <div className="muted small">Sem sugestões. Selecione trechos no documento para vincular dados.</div>;
  return (
    <div className="stack gap-8">
      <div className="muted small">Trechos que parecem dados (linhas em branco, CPF, datas, valores). Confira e vincule.</div>
      {ready.length > 1 && <button className="btn small primary" onClick={() => onApply(ready)}>Vincular as {ready.length} sugestões com dado definido</button>}
      {items.map((s) => {
        const i = s.context.indexOf(s.sample);
        return (
          <div key={`${s.part}${s.idx}${s.start}`} className="tpl-sug">
            <div className="small">…{s.context.slice(0, i)}<mark>{s.sample}</mark>{s.context.slice(i + s.sample.length)}…</div>
            <div className="row nowrap small">
              <span className="grow">{s.field ? <>→ <b>{labels.get(s.field) ?? s.field}</b></> : s.ask ? <>→ perguntar “{s.ask.label}”</> : <span className="muted">escolha o dado</span>}</span>
              {s.field ? <button className="btn small primary" onClick={() => onApply([s])}>Vincular</button> : null}
              <button className="btn small" onClick={() => onPick(s)}>{s.field ? "Outro" : "Escolher"}</button>
              <button className="btn small ghost" onClick={() => onIgnore(s)}>Ignorar</button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------- escolher dado

const slug = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "").slice(0, 40) || "pergunta";

function FieldPicker({ data, insideLoop, sample, title, busy, onClose, onPick }: {
  data: EditorData; insideLoop: string | null; sample: string; title: string; busy: boolean;
  onClose: () => void; onPick: (key: string, filter: string | null, input?: InputDef) => void;
}): JSX.Element {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<string | null>(null);
  const [ask, setAsk] = useState<{ label: string; type: InputDef["type"]; required: boolean } | null>(null);
  const f = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
  const match = (l: string, k: string) => !q || f(l).includes(f(q)) || k.includes(f(q));
  const groups = [
    ...(insideLoop ? [{ label: `Cada ${LIST_LABEL[insideLoop] ?? insideLoop} (repetição)`, fields: Object.entries(data.catalog.items[insideLoop] ?? {}).map(([k, l]) => ({ key: `item.${k}`, label: l })) }] : []),
    ...(data.inputs.length ? [{ label: "Perguntas deste modelo", fields: data.inputs.flatMap((i) => [{ key: `campo.${i.key}`, label: i.label }, ...(i.type === "dinheiro" || i.type === "data" ? [{ key: `campo.${i.key}_extenso`, label: `${i.label} (por extenso)` }] : [])]) }] : []),
    ...data.catalog.groups,
  ];
  return (
    <Modal wide title={title} onClose={onClose}>
      <div className="stack gap-8">
        {sample && <div className="small">Trecho selecionado: <mark>{sample}</mark></div>}
        <div className="row nowrap">
          <input className="input grow" autoFocus placeholder="Buscar dado (ex.: CPF, endereço, processo)…" value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="input" style={{ width: 170 }} value={filter ?? ""} onChange={(e) => setFilter(e.target.value || null)}>
            <option value="">Como está no cadastro</option><option value="maiusculas">MAIÚSCULAS</option><option value="minusculas">minúsculas</option>
          </select>
        </div>
        <div className="tpl-fields">
          {groups.map((g) => {
            const fields = g.fields.filter((x) => match(x.label, x.key));
            if (!fields.length) return null;
            return (
              <div key={g.label} className="tpl-fgroup">
                <div className="fld-label">{g.label}</div>
                {fields.map((x) => <button key={x.key} className="tpl-fbtn" disabled={busy} onClick={() => onPick(x.key, filter)}>{x.label}</button>)}
              </div>
            );
          })}
        </div>
        <div className="card compact">
          {!ask ? (
            <button className="btn small" onClick={() => setAsk({ label: sample && !/^[_.\s]+$/.test(sample) ? "" : "", type: "texto", required: true })}>+ Perguntar na hora de gerar (dado que não está no cadastro)</button>
          ) : (
            <FormGrid cols={3}>
              <Field label="Pergunta" required><input className="input" autoFocus value={ask.label} onChange={(e) => setAsk({ ...ask, label: e.target.value })} placeholder="Ex.: Objeto da ação" /></Field>
              <Field label="Tipo">
                <select className="input" value={ask.type} onChange={(e) => setAsk({ ...ask, type: e.target.value as InputDef["type"] })}>
                  <option value="texto">Texto curto</option><option value="textolongo">Texto longo</option><option value="dinheiro">Valor (R$)</option>
                  <option value="data">Data</option><option value="numero">Número</option><option value="simnao">Sim / Não</option>
                </select>
              </Field>
              <Field label=" ">
                <div className="row nowrap">
                  <label className="row nowrap small"><input type="checkbox" checked={ask.required} onChange={(e) => setAsk({ ...ask, required: e.target.checked })} /> obrigatória</label>
                  <button className="btn small primary" disabled={ask.label.trim().length < 2 || busy} onClick={() => {
                    let key = slug(ask.label);
                    for (let i = 2; data.inputs.some((x) => x.key === key); i++) key = `${slug(ask.label).slice(0, 36)}_${i}`;
                    onPick(`campo.${key}`, filter, { key, label: ask.label.trim(), type: ask.type, required: ask.required, default: null });
                  }}>Criar e vincular</button>
                </div>
              </Field>
            </FormGrid>
          )}
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------- concordância

function GenderDialog({ sample, busy, onClose, onSave }: { sample: string; busy: boolean; onClose: () => void; onSave: (m: string, f: string, pl: string) => void }): JSX.Element {
  const g = guessGender(sample);
  const [m, setM] = useState(g[0]);
  const [f, setF] = useState(g[1]);
  const [pl, setPl] = useState(g[2]);
  return (
    <Modal title="Concordância com o cliente" onClose={onClose}
      footer={<><button className="btn" onClick={onClose}>Cancelar</button><button className="btn primary" disabled={!m.trim() || !f.trim() || busy} onClick={() => onSave(m.trim(), f.trim(), pl.trim())}>Aplicar</button></>}>
      <div className="stack gap-8">
        <div className="small">O documento usa a forma certa pelo gênero do cadastro do cliente (e o plural quando houver mais de um cliente).</div>
        <FormGrid cols={3}>
          <Field label="Homem"><input className="input" value={m} onChange={(e) => setM(e.target.value)} placeholder="portador" /></Field>
          <Field label="Mulher"><input className="input" value={f} onChange={(e) => setF(e.target.value)} placeholder="portadora" /></Field>
          <Field label="Vários clientes (opcional)"><input className="input" value={pl} onChange={(e) => setPl(e.target.value)} placeholder="portadores" /></Field>
        </FormGrid>
        <div className="muted small">Sem gênero no cadastro sai “{m}(…)” ou “{m}/{f}”.</div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------- regra

const NEEDS: Record<string, "acao" | "campo" | "pergunta" | null> = {
  tipo_acao: "acao", campo_preenchido: "campo", campo_vazio: "campo", resposta_sim: "pergunta", resposta_nao: "pergunta",
};

function RuleDialog({ data, busy, onClose, onSave }: {
  data: EditorData; busy: boolean; onClose: () => void;
  onSave: (rule: { kind: string; value: string | null }, alt: string, input?: InputDef) => void;
}): JSX.Element {
  const [kind, setKind] = useState("tem_parceiro");
  const [value, setValue] = useState("");
  const [alt, setAlt] = useState("");
  const [newQ, setNewQ] = useState("");
  const need = NEEDS[kind] ?? null;
  const yesno = data.inputs.filter((i) => i.type === "simnao");
  const ok = !need || (need === "pergunta" ? !!value || newQ.trim().length >= 2 : !!value);
  return (
    <Modal wide title="Mostrar este trecho só quando…" onClose={onClose}
      footer={<><button className="btn" onClick={onClose}>Cancelar</button><button className="btn primary" disabled={!ok || busy} onClick={() => {
        if (need === "pergunta" && !value) {
          let key = slug(newQ);
          for (let i = 2; data.inputs.some((x) => x.key === key); i++) key = `${slug(newQ).slice(0, 36)}_${i}`;
          onSave({ kind, value: `campo.${key}` }, alt, { key, label: newQ.trim(), type: "simnao", required: true, default: null });
        } else onSave({ kind, value: need ? value : null }, alt);
      }}>Criar regra</button></>}>
      <div className="gen-layout" style={{ gridTemplateColumns: "1fr 1fr" }}>
        <div className="stack gap-4">
          {Object.entries(data.catalog.ruleKinds).map(([k, l]) => (
            <label key={k} className="row nowrap small"><input type="radio" checked={kind === k} onChange={() => { setKind(k); setValue(""); }} /> {l}{NEEDS[k] ? "…" : ""}</label>
          ))}
        </div>
        <div className="stack gap-8">
          {need === "acao" && (
            <Field label="Tipo de ação">
              <select className="input" value={value} onChange={(e) => setValue(e.target.value)}>
                <option value="">Escolha…</option>{data.catalog.actionTypes.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </Field>
          )}
          {need === "campo" && (
            <Field label="Dado">
              <select className="input" value={value} onChange={(e) => setValue(e.target.value)}>
                <option value="">Escolha…</option>
                {data.inputs.map((i) => <option key={i.key} value={`campo.${i.key}`}>Pergunta: {i.label}</option>)}
                {data.catalog.groups.map((g) => <optgroup key={g.label} label={g.label}>{g.fields.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}</optgroup>)}
              </select>
            </Field>
          )}
          {need === "pergunta" && (
            <>
              {yesno.length > 0 && (
                <Field label="Pergunta Sim/Não deste modelo">
                  <select className="input" value={value} onChange={(e) => setValue(e.target.value)}>
                    <option value="">Nova pergunta…</option>{yesno.map((i) => <option key={i.key} value={`campo.${i.key}`}>{i.label}</option>)}
                  </select>
                </Field>
              )}
              {!value && <Field label="Nova pergunta (Sim/Não) feita na hora de gerar"><input className="input" value={newQ} onChange={(e) => setNewQ(e.target.value)} placeholder="Ex.: Incluir cláusula de êxito?" /></Field>}
            </>
          )}
          <Field label="Senão, mostrar este texto (opcional)" hint="Aparece no lugar quando a condição não vale.">
            <textarea className="input" rows={3} value={alt} onChange={(e) => setAlt(e.target.value)} />
          </Field>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------- propriedades

function PropsDialog({ data, onClose }: { data: EditorData; onClose: () => void }): JSX.Element {
  const t = data.template;
  const [f, setF] = useState({ name: t.name, description: t.description ?? "", context: t.context, letterhead: t.letterhead, letterheadId: t.letterheadId, pdfEngine: t.pdfEngine, required: t.required, active: t.active });
  const lhs = useQuery({ queryKey: ["timbrados"], queryFn: () => api.get<Array<{ id: string; name: string; isDefault: boolean }>>("/timbrados") });
  const fileRef = useRef<HTMLInputElement>(null);
  const save = useAct(() => api.patch(`/config/modelos/${t.id}`, { ...f, description: f.description || null }), [["modelo-editor", t.id], ["config-modelos"], ["modelos-documento"]], onClose);
  const upload = useAct(async (file: File) => { const fd = new FormData(); fd.append("file", file); return api.upload(`/config/modelos/${t.id}/arquivo`, fd); },
    [["modelo-editor", t.id], ["config-modelos"]], onClose, "Arquivo trocado (a versão anterior fica no histórico)");
  const reqFields = data.catalog.groups.filter((g) => g.label === "Cliente" || g.label === "Processo").flatMap((g) => g.fields);
  return (
    <Modal wide title="Propriedades do modelo" onClose={onClose}
      footer={<><button className="btn" onClick={onClose}>Cancelar</button><button className="btn primary" disabled={f.name.trim().length < 2 || save.isPending} onClick={() => save.mutate()}>Salvar</button></>}>
      <FormGrid cols={3}>
        <Field label="Nome" required><input className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
        <Field label="Gerado a partir de">
          <select className="input" value={f.context} onChange={(e) => setF({ ...f, context: e.target.value as typeof f.context })}>
            <option value="pessoa">Pessoa (ou caso/processo)</option><option value="caso">Caso (ou processo)</option><option value="processo">Processo</option>
          </select>
        </Field>
        <Field label="Timbrado">
          <select className="input" value={!f.letterhead ? "none" : f.letterheadId ?? ""} onChange={(e) => { const v = e.target.value; setF({ ...f, letterhead: v !== "none", letterheadId: v && v !== "none" ? v : null }); }}>
            <option value="">Automático (unidade → padrão)</option>
            {(lhs.data ?? []).map((l) => <option key={l.id} value={l.id}>Sempre: {l.name}</option>)}
            <option value="none">Sem timbrado</option>
          </select>
        </Field>
        <Field label="Descrição" span={2}><input className="input" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
        <Field label="PDF" hint="“Igual ao Word” usa o LibreOffice do servidor; sem ele, sai pelo motor do sistema.">
          <select className="input" value={f.pdfEngine} onChange={(e) => setF({ ...f, pdfEngine: e.target.value as "word" | "interno" })}>
            <option value="interno">Motor do sistema (padrão dos modelos atuais)</option><option value="word">Igual ao Word (LibreOffice)</option>
          </select>
        </Field>
      </FormGrid>
      <div className="fld-label" style={{ marginTop: 12 }}>Dados obrigatórios do cadastro (avisa antes de gerar)</div>
      <div className="chips">
        {reqFields.map((x) => {
          const on = f.required.includes(x.key);
          return <button key={x.key} type="button" className={`chip ${on ? "on" : ""}`} onClick={() => setF({ ...f, required: on ? f.required.filter((k) => k !== x.key) : [...f.required, x.key] })}>{x.label}</button>;
        })}
      </div>
      <div className="row" style={{ marginTop: 12 }}>
        <label className="row nowrap"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} /> Modelo ativo (aparece em “Gerar documento”)</label>
        <span className="spacer" />
        <input ref={fileRef} type="file" hidden accept=".docx" onChange={(e) => { const x = e.target.files?.[0]; if (x) upload.mutate(x); e.target.value = ""; }} />
        <button className="btn small" onClick={() => fileRef.current?.click()}>Trocar arquivo Word…</button>
      </div>
      <ErrorBox error={save.error ?? upload.error} />
    </Modal>
  );
}

// ---------------------------------------------------------------- teste

function TestDialog({ data, onClose }: { data: EditorData; onClose: () => void }): JSX.Element {
  const [mode, setMode] = useState<"exemplo" | "real">("exemplo");
  const [ex, setEx] = useState({ genero: "F" as "F" | "M", clientes: 1, parceiro: false, pj: false, respostas: {} as Record<string, string> });
  const [ref, setRef] = useState<EntityRef | null>(null);
  const [partnerIds, setPartnerIds] = useState<string[]>([]);
  const partners = useQuery({ queryKey: ["parceiros-ativos"], queryFn: () => api.get<Array<{ id: string; name: string }>>("/financeiro/parceiros/ativos") });
  const sample = useQuery({
    queryKey: ["modelo-exemplo", data.template.id, data.template.version, ex], enabled: mode === "exemplo",
    queryFn: () => api.post<{ blocks: Block[]; empty: string[] }>(`/config/modelos/${data.template.id}/editor/exemplo`, ex),
  });
  const real = useQuery({
    queryKey: ["modelo-real", data.template.id, data.template.version, ref, partnerIds, ex.respostas], enabled: mode === "real" && !!ref,
    queryFn: () => api.post<{ blocks: Block[] | null; missing: Array<{ label: string }> }>("/documentos/gerar/previa", {
      templateId: data.template.id, personId: ref?.kind === "pessoa" ? ref.id : null, caseId: ref?.kind === "caso" ? ref.id : null,
      processId: ref?.kind === "processo" ? ref.id : null, partnerIds, inputs: ex.respostas,
    }),
    retry: false,
  });
  const blocks = mode === "exemplo" ? sample.data?.blocks : real.data?.blocks;
  return (
    <Modal wide title="Testar o modelo" onClose={onClose}>
      <div className="gen-layout">
        <div className="stack gap-8">
          <div className="tabs">
            <button className={`tab ${mode === "exemplo" ? "active" : ""}`} onClick={() => setMode("exemplo")}>Dados de exemplo</button>
            <button className={`tab ${mode === "real" ? "active" : ""}`} onClick={() => setMode("real")}>Cliente real</button>
          </div>
          {mode === "exemplo" ? (
            <>
              <Field label="Cliente">
                <div className="row nowrap small">
                  <label><input type="radio" checked={ex.genero === "F"} onChange={() => setEx({ ...ex, genero: "F" })} /> mulher</label>
                  <label><input type="radio" checked={ex.genero === "M"} onChange={() => setEx({ ...ex, genero: "M" })} /> homem</label>
                  <label><input type="checkbox" checked={ex.pj} onChange={(e) => setEx({ ...ex, pj: e.target.checked })} /> empresa</label>
                </div>
              </Field>
              <Field label="Quantos clientes"><select className="input" value={ex.clientes} onChange={(e) => setEx({ ...ex, clientes: Number(e.target.value) })}><option value={1}>1</option><option value={2}>2</option><option value={3}>3</option></select></Field>
              <label className="row nowrap small"><input type="checkbox" checked={ex.parceiro} onChange={(e) => setEx({ ...ex, parceiro: e.target.checked })} /> Com advogado parceiro</label>
            </>
          ) : (
            <>
              <Field label="Pessoa, caso ou processo"><EntityPicker kinds={["pessoa", "caso", "processo"]} value={ref} onChange={setRef} /></Field>
              {(partners.data?.length ?? 0) > 0 && (
                <Field label="Parceiros">
                  <div className="chips">{partners.data!.map((p) => { const on = partnerIds.includes(p.id); return <button key={p.id} type="button" className={`chip ${on ? "on" : ""}`} onClick={() => setPartnerIds(on ? partnerIds.filter((x) => x !== p.id) : [...partnerIds, p.id])}>{p.name}</button>; })}</div>
                </Field>
              )}
            </>
          )}
          {data.inputs.map((i) => (
            <Field key={i.key} label={i.label}>
              {i.type === "simnao"
                ? <select className="input" value={ex.respostas[i.key] ?? ""} onChange={(e) => setEx({ ...ex, respostas: { ...ex.respostas, [i.key]: e.target.value } })}><option value="">(Sim)</option><option value="Sim">Sim</option><option value="Não">Não</option></select>
                : <input className="input" value={ex.respostas[i.key] ?? ""} onChange={(e) => setEx({ ...ex, respostas: { ...ex.respostas, [i.key]: e.target.value } })} placeholder="(exemplo)" />}
            </Field>
          ))}
          {mode === "real" && (real.data?.missing.length ?? 0) > 0 && <div className="warn-box small">Faltam no cadastro: {real.data!.missing.map((m) => m.label).join(", ")}</div>}
          <ErrorBox error={mode === "exemplo" ? sample.error : real.error} />
        </div>
        <div>{blocks ? <Paper blocks={blocks} /> : (mode === "exemplo" ? sample.isFetching : real.isFetching) ? <Loading lines={8} /> : <div className="muted pad">{mode === "real" ? "Escolha a pessoa, o caso ou o processo." : ""}</div>}</div>
      </div>
    </Modal>
  );
}

/** Novo modelo a partir de um Word. */
export function NewWordTemplate({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }): JSX.Element {
  const [name, setName] = useState("");
  const [context, setContext] = useState<"pessoa" | "caso" | "processo">("pessoa");
  const [file, setFile] = useState<File | null>(null);
  const create = useAct(async () => {
    const fd = new FormData();
    fd.append("name", name.trim()); fd.append("context", context);
    fd.append("file", file!);
    return api.upload<{ id: string }>("/config/modelos/novo-word", fd);
  }, [["config-modelos"], ["modelos-documento"]], (r) => onCreated(r.id), "Modelo criado");
  return (
    <Modal title="Novo modelo a partir de um Word" onClose={onClose}
      footer={<><button className="btn" onClick={onClose}>Cancelar</button><button className="btn primary" disabled={!file || create.isPending} onClick={() => create.mutate()}>{create.isPending ? "Enviando…" : "Enviar e abrir no editor"}</button></>}>
      <div className="stack gap-12">
        <div className="small">Envie o Word que o escritório já usa (com a formatação e o timbrado). Depois, no editor, você marca onde entram os dados do sistema.</div>
        <Field label="Arquivo Word (.docx)" required>
          <input type="file" accept=".docx" onChange={(e) => { const f = e.target.files?.[0] ?? null; setFile(f); if (f && !name) setName(f.name.replace(/\.docx$/i, "")); }} />
        </Field>
        <FormGrid cols={2}>
          <Field label="Nome do modelo" required><input className="input" value={name} onChange={(e) => setName(e.target.value)} /></Field>
          <Field label="Gerado a partir de">
            <select className="input" value={context} onChange={(e) => setContext(e.target.value as typeof context)}>
              <option value="pessoa">Pessoa (ou caso/processo)</option><option value="caso">Caso (ou processo)</option><option value="processo">Processo</option>
            </select>
          </Field>
        </FormGrid>
        <ErrorBox error={create.error} />
      </div>
    </Modal>
  );
}
